import { useCallback, useState } from 'react';
import { CircleNotchIcon, LinkIcon, WalletIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { LinkMascot } from '@/components/LinkMascot';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { useSubscription } from '@/hooks/queries/useSubscription';
import { ApiError, onboardingApi } from '@/lib/api';
import { track } from '@/lib/telemetry';
import { trialDaysLeft } from './flow.helpers';

export interface StepDoneProps {
  userName: string;
  connectionsCount: number;
  /** Cards that actually WROTE a classification — per-card deferrals excluded. */
  streamsClassified: number;
  /** Per-card "Não tenho certeza" answers — named separately, never counted as classified. */
  streamsDeferred: number;
  streamsTotal: number;
  categoriesDeferred: boolean;
  onBack: () => void;
  /** Called after the server accepted the completion — the flow navigates away. */
  onCompleted: () => void;
}

export function StepDone(props: StepDoneProps) {
  const authedFetch = useAuthedFetcher();
  const [finishing, setFinishing] = useState(false);
  // Null hides the trial copy rather than guessing a number the product no
  // longer matches (the UI once said 30, the backend enforced 100, product = 7).
  const { data: subscription } = useSubscription();
  const daysLeft = trialDaysLeft(subscription?.trialEndsAt);

  // Completion is ungated — the 2026-08-22 product ruling removed the consent
  // term (the backend gate went with it; deploy order: api first).
  const handleFinish = useCallback(async () => {
    if (finishing) return;
    setFinishing(true);
    try {
      await authedFetch((token) => onboardingApi.complete(token));
      track('action.onboarding.completed', {
        connections: props.connectionsCount,
        streams_classified: props.streamsClassified,
      });
      props.onCompleted();
    } catch (err) {
      setFinishing(false);
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível concluir a configuração.');
    }
  }, [authedFetch, finishing, props]);

  return (
    <section>
      <div className="flex items-center gap-3.5">
        <LinkMascot state="core" size="sm" />
        <div className="min-w-0">
          <h1 className="text-[22px] font-bold tracking-tight">
            Tudo pronto, <span className="text-accent-brand">{props.userName}</span>.
          </h1>
          {daysLeft !== null && (
            <p className="mt-1 font-mono text-[11px] text-text-tertiary">
              {daysLeft === 1 ? 'resta 1 dia' : `restam ${daysLeft} dias`} de teste · sem cartão
            </p>
          )}
        </div>
      </div>

      <div className="mt-5">
        <Card className="p-5">
          <p className="font-mono text-2xs tracking-[0.14em] text-text-tertiary uppercase">
            O que já está de pé
          </p>
          <ul className="mt-2">
            <SummaryRow
              icon={<LinkIcon size={16} weight="duotone" />}
              text="Instituições conectadas"
              value={String(props.connectionsCount)}
            />
            {/* Both ratios describe the CURRENT worklist, never an all-time total:
                confirmed answers live on their own resources and settled items stop
                being questions, so on a cross-device resume the worklist is exactly
                what the server still asks — an empty one means nothing is pending,
                not that nothing was ever done. */}
            <SummaryRow
              icon={<WalletIcon size={16} weight="duotone" />}
              text={
                props.streamsTotal === 0
                  ? 'Grupos classificados · nada pendente'
                  : props.categoriesDeferred || props.streamsDeferred > 0 || props.streamsClassified < props.streamsTotal
                    ? 'Grupos classificados · o resto fica na revisão do app'
                    : 'Grupos classificados'
              }
              value={props.streamsTotal === 0 ? '✓' : `${props.streamsClassified}/${props.streamsTotal}`}
            />
          </ul>
        </Card>
      </div>

      <TrialNotice trialDaysLeft={daysLeft} />

      <p className="mt-3 text-xs/5 text-pretty text-text-tertiary">
        Ao continuar, você concorda com o{' '}
        <a href="https://vertice.finance/termos" target="_blank" rel="noreferrer" className="text-brand-700 underline underline-offset-2">
          Termo de Uso
        </a>{' '}
        e a{' '}
        <a href="https://vertice.finance/privacidade" target="_blank" rel="noreferrer" className="text-brand-700 underline underline-offset-2">
          Política de Privacidade
        </a>
        . Você pode revogar o acesso aos seus dados quando quiser, nas configurações.
      </p>

      <div className="mt-6 flex items-center justify-between gap-3 border-t border-border-subtle pt-4">
        <Button variant="ghost" disabled={finishing} onClick={props.onBack}>
          Voltar
        </Button>
        <Button disabled={finishing} onClick={handleFinish}>
          {finishing && <CircleNotchIcon size={16} className="animate-spin" />}
          Ver meu painel
        </Button>
      </div>
    </section>
  );
}

/** The billing reassurance holds a number only while the trial claim is live. */
function TrialNotice({ trialDaysLeft }: { trialDaysLeft: number | null }) {
  if (trialDaysLeft === null) return null;
  return (
    <div className="mt-3 rounded-2xl border border-info-border bg-info-surface px-4 py-3.5">
      <p className="text-[13px] font-semibold text-info-text">
        Cobrança só se você continuar depois dos {trialDaysLeft} {trialDaysLeft === 1 ? 'dia' : 'dias'} restantes
      </p>
      <p className="mt-1 text-xs/5 text-pretty text-info-text/85">
        Quando você decidir assinar, a gente pede seu endereço para emitir a nota fiscal. Nada disso agora.</p>
    </div>
  );
}

function SummaryRow({ icon, text, value }: { icon: React.ReactNode; text: string; value: string }) {
  return (
    <li className="flex items-center gap-3 border-b border-divider py-2">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-primary">
        {icon}
      </span>
      <span className="min-w-0 flex-1 text-sm font-medium">{text}</span>
      <span className="shrink-0 font-mono text-sm tabular-nums">{value}</span>
    </li>
  );
}
